import {
    flashClasses,
    focusGuessInput,
    getEditableLetterTiles,
    getGuessForm,
    getGuessInput,
    getScrollParent,
    scrollIntoView,
    shakeTiles,
} from './dom.js'

const NON_LETTERS = /[^a-zA-Z]/g

// Highlights a tile as a letter is typed or pasted into it. `!important` so
// the border beats the tile's base one.
const POP_CLASSES = ['animate-pop', 'border-gray-800!']

const input = getGuessInput()

const getRowLetters = (tiles) =>
    tiles.map((tile) => tile.querySelector('[data-tile-value]').value).join('')

const writeRowLetters = (tiles, letters) => {
    tiles.forEach((tile, index) => {
        const letter = letters[index] ?? ''
        tile.querySelector('[data-tile-letter]').textContent = letter
        tile.querySelector('[data-tile-value]').value = letter
    })
}

// Set between compositionstart/end, while an IME (e.g. Android's Gboard,
// which composes whole words) owns the input's value.
let composing = false

// The tile the next letter goes into. Once the row is full the last tile
// keeps it, so focus stays visible.
const getCursorTile = (tiles) =>
    tiles[Math.min(getRowLetters(tiles).length, tiles.length - 1)]

const refreshCursor = () => {
    const tiles = getEditableLetterTiles()
    const cursorTile = document.activeElement === input && getCursorTile(tiles)
    tiles.forEach((tile) =>
        tile.classList.toggle('tile-cursor', tile === cursorTile)
    )
}

// Clear has nothing to do while the row is empty. The button is swapped
// with the form, so it's looked up each time.
const refreshClearButton = (tiles) => {
    const button = document.getElementById('guess-clear')
    if (button) button.disabled = !getRowLetters(tiles)
}

// Letters are only ever added or removed at the end, which the tile cursor
// shows, so the hidden caret is kept there (against arrow keys, Home, or
// iOS's spacebar trackpad).
const pinCaretToEnd = () => {
    const end = input.value.length
    if (input.selectionStart !== end || input.selectionEnd !== end) {
        input.setSelectionRange(end, end)
    }
}

/**
 * Copies the input's letters into the active row's tiles and hidden fields,
 * dropping anything that isn't a letter or doesn't fit. The input is
 * rewritten to match, so it never holds more than the row can show - except
 * mid-composition, when rewriting it would garble the IME's word, so only
 * the tiles are updated until compositionend.
 */
const syncRow = ({ submitIfFull = false } = {}) => {
    const tiles = getEditableLetterTiles()
    if (!tiles.length) return

    const previous = getRowLetters(tiles)
    const raw = input.value
    const cleaned = raw.replace(NON_LETTERS, '')
    const letters = cleaned.toUpperCase().slice(0, tiles.length)

    if (!composing) input.value = letters
    writeRowLetters(tiles, letters)

    for (let i = 0; i < letters.length; i++) {
        if (letters[i] !== previous[i])
            flashClasses([tiles[i]], POP_CLASSES, 150)
    }
    if (cleaned.length !== raw.length) shakeTiles([getCursorTile(tiles)])
    refreshCursor()
    refreshClearButton(tiles)

    // An incomplete row is left for the server to reject (GUESS_REJECTED,
    // see guess-submitted.js), so that feedback lives in one place.
    if (submitIfFull && letters.length === tiles.length) {
        input.form?.requestSubmit()
    }
}

/**
 * Points the input at whichever row is now active. htmx swaps in a fresh
 * row after every guess (pre-filled with an invalid guess's letters, so it
 * can be corrected), or none at all once the game is finished.
 */
const attachToActiveRow = () => {
    const tiles = getEditableLetterTiles()
    if (!tiles.length) {
        input.disabled = true
        input.blur()
        return
    }

    input.disabled = false
    input.value = getRowLetters(tiles)
    refreshCursor()
    refreshClearButton(tiles)
}

if (input) {
    // Keeps the row and its Submit/Clear buttons clear of the on-screen
    // keyboard. Runs a frame late so it follows the browser's own scrolling
    // (which, when typing, only brings the hidden input into view), and only
    // the latest request per frame is acted on, as resize fires every frame
    // of the keyboard's animation.
    let scrollFrame = null
    const keepFormInView = () => {
        cancelAnimationFrame(scrollFrame)
        scrollFrame = requestAnimationFrame(() =>
            scrollIntoView(getGuessForm())
        )
    }

    input.addEventListener('input', (event) => {
        syncRow({ submitIfFull: event.inputType === 'insertFromPaste' })
        keepFormInView()
    })
    input.addEventListener('compositionstart', () => {
        composing = true
    })
    input.addEventListener('compositionend', () => {
        composing = false
        syncRow()
    })

    // Enter/Go submits through the browser's implicit submission, as the
    // input belongs to the form via its form="guess-form" attribute.

    document.addEventListener('selectionchange', () => {
        if (document.activeElement === input && !composing) pinCaretToEnd()
    })

    input.addEventListener('focus', refreshCursor)
    input.addEventListener('blur', refreshCursor)

    // Tapping the row, or Clear, has to focus the input there and then,
    // inside the tap, for iOS to open the keyboard. Both are swapped out
    // with the form after every guess, so their clicks are delegated.
    document.addEventListener('click', (event) => {
        if (event.target.closest('#guess-clear')) {
            input.value = ''
            syncRow()
        }
        if (event.target.closest('#active-row, #guess-clear')) focusGuessInput()
    })

    // Mobile keyboards cover the page without shrinking it, so while one is
    // open for the input, the scroll container (<main>) is padded by its
    // height. Otherwise a form near the end of the page can't scroll far
    // enough to clear it. Scaling by zoom keeps a pinch-zoom from counting
    // as a keyboard.
    const padForKeyboard = () => {
        const viewport = window.visualViewport
        const container = getScrollParent(input)
        if (!viewport || !container) return
        const keyboard =
            document.activeElement === input
                ? Math.round(
                      window.innerHeight - viewport.height * viewport.scale
                  )
                : 0
        container.style.paddingBottom = keyboard > 0 ? `${keyboard}px` : ''
    }

    // Once the keyboard has opened (or resized), make sure it isn't
    // covering the form. Closing it drops the padding again.
    window.visualViewport?.addEventListener('resize', () => {
        padForKeyboard()
        if (document.activeElement === input) keepFormInView()
    })

    document.body.addEventListener('htmx:after:settle', attachToActiveRow)
    attachToActiveRow()
}
